import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { Locale } from '@diana/shared'
import { useContent } from '../../hooks/useContent.js'
import { resolveAboutPhoto } from '../../lib/photos.js'
import { SectionHead } from '../../components/Section.js'
import { useLocale, usePath } from '../../i18n/index.js'

/**
 * Structure follows the reference: Cześć! → O mnie → Prywatnie.
 *
 * ⚠️ Every word below is a PLACEHOLDER in Diana's voice-to-be. The entire
 * positioning rests on this sounding like her and nobody else — see
 * docs/01-WEBSITE.md §2. She rewrites it; we edit after.
 */

type PageStrings = {
  eyebrow: string
  heading: string
  intro1: string
  intro2: string
  intro3: string
  proEyebrow: string
  proTitle: string
  pro1: string
  pro2: string
  pro3: string
  pro4: string
  privEyebrow: string
  privTitle: string
  priv1: string
  priv2: string
  fact1: string
  fact2: string
  fact3: string
  ctaHeading: ReactNode
  ctaBody: string
  ctaButton: string
}

const T: Record<Locale, PageStrings> = {
  pl: {
    eyebrow: 'O mnie',
    heading: 'Cześć!',
    intro1:
      'Nazywam się Diana i uczę polskiego obcokrajowców. Nie tego z podręcznika — tego, którym ludzie naprawdę mówią w pracy, w sklepie i przy stole u rodziny.',
    intro2:
      'Najlepiej wychodzi mi wyłapywanie kalek — tych zdań, które są gramatycznie poprawne, ale żaden Polak nigdy by tak nie powiedział. To zwykle jedyna rzecz, która dzieli „mówi po polsku" od „brzmi jak Polak".',
    intro3:
      'Jeśli chcesz uczciwej informacji zwrotnej zamiast miłego „bardzo dobrze!" po każdym zdaniu — to dobrze trafiłeś.',
    proEyebrow: 'Zawodowo',
    proTitle: 'Jak uczę',
    pro1:
      'Zaczęłam uczyć jeszcze na studiach. Skończyłam filologię i pracowałam z tekstem w bardzo różnych rejestrach — od urzędowego po zupełnie potoczny. To dało mi ucho na styl, którego nie da się wyćwiczyć z jednego podręcznika.',
    pro2:
      'Uczyłam się metodyki od lektorów z całego świata. Najbliżej mi do podejścia bezpodręcznikowego i metody bezpośredniej, z naciskiem na słownictwo — bo to ono, a nie tabelka odmian, decyduje o tym, czy dogadasz się w piątek wieczorem.',
    pro3:
      'Lekcja to coś więcej niż słówka, gramatyka i zadania. To przede wszystkim logika — zrozumienie, dlaczego język działa tak, a nie inaczej.',
    pro4:
      'Dlatego prawie nigdy nie odpowiadam „bo tak jest". Zawsze da się wytłumaczyć, skąd się coś wzięło — a rzeczy zrozumiane zostają w głowie znacznie dłużej niż wykute.',
    privEyebrow: 'Prywatnie',
    privTitle: 'Poza lekcjami',
    priv1:
      'Sama uczę się języków, więc dobrze wiem, jak to jest siedzieć po drugiej stronie i nie rozumieć połowy zdania. Pamiętam ten moment, w którym chce się po prostu przestać.',
    priv2:
      'Lubię podróżować sama — to najszybszy sposób, żeby przekonać się, ile naprawdę potrafisz powiedzieć w obcym języku, kiedy nie ma kogo poprosić o pomoc.',
    fact1: 'Mam dwa koty',
    fact2: 'Nie znoszę brukselki',
    fact3: 'Oglądam Formułę 1',
    ctaHeading: (
      <>
        Poznajmy się <em className="not-italic text-gold-300">na żywo</em>
      </>
    ),
    ctaBody:
      '30 minut, bezpłatnie, bez zobowiązań. Zobaczysz, jak pracuję, a ja zobaczę, czego potrzebujesz.',
    ctaButton: 'Umów rozmowę',
  },
  uk: {
    eyebrow: 'Про мене',
    heading: 'Привіт!',
    intro1:
      'Мене звати Діана, і я навчаю польської іноземців. Не тієї, що в підручнику — а тієї, якою люди справді говорять на роботі, в магазині й за столом у родини.',
    intro2:
      'Найкраще мені вдається виловлювати кальки — речення, які граматично правильні, але жоден поляк так ніколи не скаже. Зазвичай саме це відділяє «говорить польською» від «звучить як поляк».',
    intro3:
      'Якщо хочеш чесного зворотного зв’язку замість милого «дуже добре!» після кожного речення — то ти за адресою.',
    proEyebrow: 'Професійно',
    proTitle: 'Як я навчаю',
    pro1:
      'Я почала викладати ще в університеті. Закінчила філологію і працювала з текстами в дуже різних регістрах — від офіційного до цілком розмовного. Це дало мені відчуття стилю, якого не натренуєш за одним підручником.',
    pro2:
      'Методики я вчилася у викладачів з усього світу. Найближчі мені безпідручниковий підхід і прямий метод, з акцентом на лексику — бо саме вона, а не таблиця відмінювань, вирішує, чи порозумієшся ти в п’ятницю ввечері.',
    pro3:
      'Урок — це більше, ніж слова, граматика і вправи. Це передусім логіка — розуміння, чому мова працює саме так, а не інакше.',
    pro4:
      'Тому я майже ніколи не відповідаю «бо так є». Завжди можна пояснити, звідки щось узялося — а зрозуміле тримається в голові значно довше, ніж визубрене.',
    privEyebrow: 'Особисте',
    privTitle: 'Поза уроками',
    priv1:
      'Я сама вчу мови, тож добре знаю, як це — сидіти по той бік і не розуміти половини речення. Пам’ятаю той момент, коли хочеться просто все покинути.',
    priv2:
      'Люблю подорожувати сама — це найшвидший спосіб перевірити, скільки ти насправді можеш сказати іноземною, коли нема кого попросити про допомогу.',
    fact1: 'Маю двох котів',
    fact2: 'Терпіти не можу брюссельську капусту',
    fact3: 'Дивлюся Формулу-1',
    ctaHeading: (
      <>
        Познайомимось <em className="not-italic text-gold-300">наживо</em>
      </>
    ),
    ctaBody:
      '30 хвилин, безкоштовно, без зобов’язань. Ти побачиш, як я працюю, а я побачу, що тобі потрібно.',
    ctaButton: 'Записатися на розмову',
  },
  ru: {
    eyebrow: 'Обо мне',
    heading: 'Привет!',
    intro1:
      'Меня зовут Диана, и я учу польскому иностранцев. Не тому, что в учебнике — а тому, на котором люди действительно говорят на работе, в магазине и за семейным столом.',
    intro2:
      'Лучше всего у меня получается вылавливать кальки — предложения, которые грамматически правильны, но ни один поляк так никогда не скажет. Обычно именно это отделяет «говорит по-польски» от «звучит как поляк».',
    intro3:
      'Если хочешь честной обратной связи вместо милого «очень хорошо!» после каждой фразы — тебе ко мне.',
    proEyebrow: 'Профессионально',
    proTitle: 'Как я учу',
    pro1:
      'Я начала преподавать ещё в университете. Окончила филологию и работала с текстами в самых разных регистрах — от официального до совсем разговорного. Это дало мне чувство стиля, которое не натренируешь по одному учебнику.',
    pro2:
      'Методике я училась у преподавателей со всего мира. Мне ближе всего подход без учебника и прямой метод, с упором на лексику — потому что именно она, а не таблица склонений, решает, договоришься ли ты в пятницу вечером.',
    pro3:
      'Урок — это больше, чем слова, грамматика и упражнения. Это прежде всего логика — понимание, почему язык работает именно так, а не иначе.',
    pro4:
      'Поэтому я почти никогда не отвечаю «потому что так». Всегда можно объяснить, откуда что взялось — а понятое остаётся в голове куда дольше, чем вызубренное.',
    privEyebrow: 'Личное',
    privTitle: 'Вне уроков',
    priv1:
      'Я сама учу языки, так что хорошо знаю, каково это — сидеть по ту сторону и не понимать половину предложения. Помню тот момент, когда хочется просто всё бросить.',
    priv2:
      'Люблю путешествовать одна — это самый быстрый способ узнать, сколько ты на самом деле можешь сказать на чужом языке, когда некого попросить о помощи.',
    fact1: 'У меня два кота',
    fact2: 'Терпеть не могу брюссельскую капусту',
    fact3: 'Смотрю Формулу-1',
    ctaHeading: (
      <>
        Давай познакомимся <em className="not-italic text-gold-300">вживую</em>
      </>
    ),
    ctaBody:
      '30 минут, бесплатно, без обязательств. Ты увидишь, как я работаю, а я увижу, что тебе нужно.',
    ctaButton: 'Записаться на встречу',
  },
}

export function AboutPage() {
  const locale = useLocale()
  const to = usePath()
  const { data } = useContent(locale)
  const copy = data?.pages['o-mnie']?.content ?? {}
  const photo = resolveAboutPhoto(data?.settings ?? {})
  const s = T[locale]

  return (
    <>
      {/* ── Cześć! ────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -left-40 top-0 h-[30rem] w-[30rem] rounded-full bg-sky-100/60 blur-3xl" />

        <div className="relative mx-auto grid max-w-6xl items-center gap-14 px-5 py-20 lg:grid-cols-[0.9fr_1.1fr] lg:px-8 lg:py-28">
          {/*
            Diana's photograph — chosen from the panel (Start → Zdjęcie na
            stronie O mnie). It follows the homepage portrait unless she picks
            a different one there; the gold frame holds a quiet gradient until
            she uploads anything at all.
          */}
          <div
            className={[
              'relative mx-auto w-full max-w-sm',
              photo.url ? 'block' : 'hidden lg:block',
            ].join(' ')}
          >
            <div className="absolute -inset-3 rounded-sm border border-gold/40" />
            {photo.url ? (
              <img
                src={photo.url}
                alt={photo.alt || 'Diana'}
                width={400}
                height={500}
                className="relative aspect-[4/5] w-full rounded-sm object-cover"
              />
            ) : (
              <div className="relative aspect-[4/5] bg-gradient-to-br from-sky-100 to-sky-200" />
            )}
          </div>

          <div>
            <p className="eyebrow">{s.eyebrow}</p>
            <h1 className="h-display mt-5 text-[2.6rem] leading-[1.1] sm:text-5xl">
              {copy.heading ?? s.heading}
            </h1>
            <div className="rule-gold mt-8 w-28" />

            <div className="mt-8 space-y-5 text-[1.05rem] leading-[1.85] font-light text-ink-soft">
              <p>{copy.intro1 ?? s.intro1}</p>
              <p>{copy.intro2 ?? s.intro2}</p>
              <p>{copy.intro3 ?? s.intro3}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── O mnie: professional ──────────────────────────────────────── */}
      <section className="bg-ivory-deep py-24 lg:py-28">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-[1fr_1.3fr]">
            <SectionHead eyebrow={s.proEyebrow} title={s.proTitle} />

            <div className="space-y-5 text-[1.05rem] leading-[1.85] font-light text-ink-soft">
              <p>{copy.pro1 ?? s.pro1}</p>
              <p>{copy.pro2 ?? s.pro2}</p>
              <p className="border-l-2 border-gold pl-6 text-ink italic">{copy.pro3 ?? s.pro3}</p>
              <p>{copy.pro4 ?? s.pro4}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Prywatnie ─────────────────────────────────────────────────── */}
      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-[1fr_1.3fr]">
            <SectionHead eyebrow={s.privEyebrow} title={s.privTitle} />

            <div>
              <div className="space-y-5 text-[1.05rem] leading-[1.85] font-light text-ink-soft">
                <p>{copy.priv1 ?? s.priv1}</p>
                <p>{copy.priv2 ?? s.priv2}</p>
              </div>

              <div className="mt-10 grid gap-px overflow-hidden rounded-sm border border-sky-200 bg-sky-200 sm:grid-cols-3">
                {[copy.fact1 ?? s.fact1, copy.fact2 ?? s.fact2, copy.fact3 ?? s.fact3].map(
                  (fact) => (
                    <p key={fact} className="bg-porcelain px-6 py-7 text-center text-sm font-light">
                      {fact}
                    </p>
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────── */}
      <section className="bg-ink py-20 text-center lg:py-24">
        <div className="mx-auto max-w-2xl px-5">
          <h2 className="h-display text-3xl leading-tight text-ivory sm:text-4xl">{s.ctaHeading}</h2>
          <p className="mx-auto mt-6 max-w-lg leading-relaxed font-light text-sky-200/80">
            {s.ctaBody}
          </p>
          <Link
            to={to('/rezerwacja')}
            className="btn mt-9 border border-gold-300 bg-gold-300 text-ink hover:bg-transparent hover:text-gold-300"
          >
            {s.ctaButton}
          </Link>
        </div>
      </section>
    </>
  )
}
